/**
 * assistantOptIn.ts — typed client for the Settings → Assistant opt-in toggle.
 *
 * Track C (C-WP1). The transport is the authed oRPC namespace `assistant.optIn`
 * (apps/api/src/routes/assistant/opt-in.ts, registered in
 * apps/api/src/rpc/router.ts). The opt-in is the explicit per-user consent that
 * is the lawful basis for any recipe text reaching a model — it is platform
 * state, defaults false, and is never exported.
 *
 * Two operations:
 *   - {@link fetchAssistantOptIn} — read the current state. A read failure
 *     resolves to `null` (a quiet, non-blocking unavailable state — this is a
 *     read-only display probe, not a mutation, so a toast is wrong). The failure
 *     is logged, never silently swallowed.
 *   - {@link setAssistantOptIn} — persist a new value. This IS a mutation, so a
 *     failure THROWS (Rule 8): the caller (AssistantSection) surfaces a visible
 *     inline error and does not flip the toggle optimistically.
 *
 * Build plan ref: .plan/implementation/2026-07-10-track-c-ai-mcp-build-plan.md
 *   C-WP1 (b) — the `assistant.optIn` get/set RPC pair.
 */

import { createApiClient, type ApiClient } from "@proof/client-core/api";

/** The `assistant.optIn.get` return type, inferred from the server router. */
type AssistantOptInResult = Awaited<ReturnType<ApiClient["assistant"]["optIn"]["get"]>>;

/**
 * Where a consent transition was captured (GDPR Art. 7(1) demonstrability). The
 * server records it on the PII-clean `assistant_opt_in.set_ok` log event so the
 * record shows *where* consent was granted, not only when. Optional on the wire
 * — an omitted source stays valid.
 */
export type AssistantOptInSource = "settings" | "paste_convert";

/** Injectable client hook — the default hits the real same-origin oRPC client. */
export interface AssistantOptInTransport {
  get: () => Promise<AssistantOptInResult>;
  set: (enabled: boolean, source?: AssistantOptInSource) => Promise<AssistantOptInResult>;
}

function defaultTransport(): AssistantOptInTransport {
  // Same-origin client (`baseUrl: ""`), matching every other in-app oRPC caller.
  // credentials: include + CSRF ride via createApiClient.
  const client = createApiClient({ baseUrl: "" });
  return {
    get: () => client.assistant.optIn.get({}),
    set: (enabled: boolean, source?: AssistantOptInSource) =>
      client.assistant.optIn.set(source === undefined ? { enabled } : { enabled, source }),
  };
}

/**
 * Read the current assistant opt-in state for the signed-in user.
 *
 * Returns the boolean on success, or `null` on any transport / parse failure —
 * the caller renders a quiet, non-blocking unavailable state (toggle disabled,
 * no fabricated consent value). The failure is logged.
 */
export async function fetchAssistantOptIn(
  transport: AssistantOptInTransport = defaultTransport(),
): Promise<boolean | null> {
  try {
    const res = await transport.get();
    return res.optIn;
  } catch (err) {
    console.error("[proof.assistant-opt-in] read failed", err);
    return null;
  }
}

/**
 * Persist a new assistant opt-in value. Returns the server-confirmed boolean.
 *
 * Rule 8: on failure this THROWS (never a silent swallow) so the calling surface
 * surfaces a visible error and keeps its prior state — the displayed value must
 * only change to reflect a confirmed server write, never an optimistic guess.
 *
 * @param source - where the consent was captured; recorded server-side for
 *   Art. 7(1) demonstrability. Omitting it is valid (back-compat).
 */
export async function setAssistantOptIn(
  enabled: boolean,
  source?: AssistantOptInSource,
  transport: AssistantOptInTransport = defaultTransport(),
): Promise<boolean> {
  const res = await transport.set(enabled, source);
  return res.optIn;
}
